import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Form, InputNumber, Modal, Select, Table, Tag, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLeave } from '../../hooks/useLeave';
import { LeaveStatus } from '../../types';

const { Option } = Select;
const { TextArea } = Input;

const statusColor: Record<LeaveStatus, string> = {
  PENDING: 'orange', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'default',
};

const LeaveRequestView: React.FC = () => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const {
    leaveTypes, leaveRequests, isLoading, statusFilter,
    fetchLeaveTypes, fetchLeaveRequests, submitLeaveRequest, cancelLeaveRequest, setStatusFilter,
  } = useLeave();

  useEffect(() => {
    fetchLeaveTypes();
    fetchLeaveRequests();
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const dto = {
      ...values,
      startDate: values.dateRange[0].format('YYYY-MM-DD'),
      endDate: values.dateRange[1].format('YYYY-MM-DD'),
      employeeId: 1, // TODO: replace with current user employee ID
    };
    await submitLeaveRequest(dto);
    setOpen(false);
    form.resetFields();
  };

  const columns = [
    { title: 'Leave Type', dataIndex: 'leaveTypeName', key: 'leaveTypeName' },
    { title: 'Start', dataIndex: 'startDate', key: 'startDate' },
    { title: 'End', dataIndex: 'endDate', key: 'endDate' },
    { title: 'Days', dataIndex: 'daysRequested', key: 'daysRequested', width: 60 },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: LeaveStatus) => <Tag color={statusColor[s]}>{s}</Tag>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: any) =>
        r.status === 'PENDING' ? (
          <Button size="small" danger onClick={() => cancelLeaveRequest(r.id)}>Cancel</Button>
        ) : null,
    },
  ];

  return (
    <div className="p-6">
      <Card
        title="My Leave Requests"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Request Leave
          </Button>
        }
      >
        <div className="mb-4">
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            {(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as LeaveStatus[]).map((s) => (
              <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>
        </div>
        <Table dataSource={leaveRequests} columns={columns} rowKey="id" loading={isLoading} size="small" />
      </Card>

      <Modal title="Request Leave" open={open} onOk={handleSubmit} onCancel={() => setOpen(false)} okText="Submit">
        <Form form={form} layout="vertical">
          <Form.Item name="leaveTypeId" label="Leave Type" rules={[{ required: true }]}>
            <Select placeholder="Select leave type">
              {leaveTypes.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="Date Range" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} disabledDate={(d) => d.isBefore(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="daysRequested" label="Days Requested" rules={[{ required: true }]}>
            <InputNumber min={0.5} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <TextArea rows={3} placeholder="Optional reason" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LeaveRequestView;
